import React, { useState } from 'react';

const SKILL_CATEGORIES = [
  {
    id: 'frontend',
    title: 'Frontend Development',
    icon: '🎨',
    skills: ['React.js', 'HTML5', 'CSS3', 'JavaScript (ES6+)', 'Bootstrap', 'Responsive Design']
  },
  {
    id: 'backend',
    title: 'Backend Development',
    icon: '⚙️',
    skills: ['Node.js', 'Express.js', 'RESTful APIs', 'Authentication', 'Server-side Programming']
  },
  {
    id: 'database',
    title: 'Database',
    icon: '🗄️',
    skills: ['MongoDB', 'NoSQL Design', 'CRUD Operations', 'Data Modeling', 'Database Optimization']
  },
  {
    id: 'tools',
    title: 'Tools & Technologies',
    icon: '🛠️',
    skills: ['Git & GitHub', 'VS Code', 'npm', 'Postman', 'MongoDB Compass']
  },
  {
    id: 'languages',
    title: 'Programming Languages',
    icon: '💻',
    skills: ['JavaScript', 'Java', 'C', 'Python (Basic)']
  },
  {
    id: 'ai',
    title: 'AI Tools',
    icon: '🤖',
    skills: ['ChatGPT', 'Claude AI', 'Google Gemini', 'AI-Assisted Development']
  }
];

const Skills = () => {
  const [activeFilter, setActiveFilter] = useState('all');

  const filteredCategories = activeFilter === 'all'
    ? SKILL_CATEGORIES
    : SKILL_CATEGORIES.filter(cat => cat.id === activeFilter);

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Syne:wght@400;600;700;800&family=Space+Mono:wght@400;700&display=swap');

        :root {
          --accent:  #00f5d4;
          --accent2: #7b61ff;
          --accent3: #ff4ecd;
          --bg:      #07090f;
          --text:    #e8eaf6;
          --muted:   rgba(232,234,246,0.45);
          --border:  rgba(0,245,212,0.13);
          --card-bg: rgba(255,255,255,0.03);
        }

        .sk-section {
          position: relative;
          padding: 6rem 2rem;
          background: var(--bg);
          font-family: 'Syne', sans-serif;
          overflow: hidden;
        }

        .sk-section::before {
          content: '';
          position: absolute;
          inset: 0;
          z-index: 0;
          background:
            radial-gradient(ellipse 50% 60% at 80% 20%, rgba(123,97,255,0.1) 0%, transparent 70%),
            radial-gradient(ellipse 40% 50% at 10% 80%, rgba(0,245,212,0.07) 0%, transparent 70%);
          pointer-events: none;
        }

        .sk-section::after {
          content: '';
          position: absolute;
          inset: 0;
          z-index: 0;
          background: repeating-linear-gradient(
            0deg,
            transparent, transparent 2px,
            rgba(0,245,212,0.015) 2px, rgba(0,245,212,0.015) 4px
          );
          pointer-events: none;
        }

        .sk-inner {
          position: relative;
          z-index: 1;
          max-width: 1280px;
          margin: 0 auto;
        }

        .sk-heading {
          text-align: center;
          margin-bottom: 4rem;
          opacity: 0;
          animation: skFadeUp 0.6s ease 0.1s forwards;
        }

        .sk-tag {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          font-family: 'Space Mono', monospace;
          font-size: 0.68rem;
          letter-spacing: 0.2em;
          text-transform: uppercase;
          color: var(--accent);
          margin-bottom: 1rem;
        }

        .sk-tag::before, .sk-tag::after {
          content: '';
          display: inline-block;
          width: 20px;
          height: 1.5px;
          background: var(--accent);
          box-shadow: 0 0 8px var(--accent);
        }

        .sk-main-title {
          font-size: clamp(1.8rem, 4vw, 2.8rem);
          font-weight: 800;
          letter-spacing: -0.03em;
          color: #fff;
        }

        .sk-main-title .sk-accent {
          background: linear-gradient(90deg, var(--accent) 0%, var(--accent2) 60%, var(--accent3) 100%);
          -webkit-background-clip: text;
          background-clip: text;
          color: transparent;
          background-size: 200% auto;
          animation: skGrad 4s linear infinite;
        }

        .sk-filters {
          display: flex;
          flex-wrap: wrap;
          justify-content: center;
          gap: 0.8rem;
          margin-bottom: 3rem;
          opacity: 0;
          animation: skFadeUp 0.6s ease 0.2s forwards;
        }

        .sk-filter-btn {
          background: rgba(255,255,255,0.03);
          border: 1px solid rgba(0,245,212,0.2);
          border-radius: 40px;
          padding: 0.5rem 1.2rem;
          font-family: 'Space Mono', monospace;
          font-size: 0.7rem;
          font-weight: 600;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          color: var(--text);
          cursor: pointer;
          transition: all 0.2s ease;
          backdrop-filter: blur(4px);
        }

        .sk-filter-btn:hover {
          border-color: var(--accent);
          background: rgba(0,245,212,0.08);
          transform: translateY(-2px);
        }

        .sk-filter-btn.active {
          background: var(--accent);
          border-color: var(--accent);
          color: var(--bg);
          box-shadow: 0 0 12px rgba(0,245,212,0.4);
        }

        .sk-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(330px, 1fr));
          gap: 1.8rem;
        }

        .sk-card {
          background: var(--card-bg);
          border: 1px solid var(--border);
          border-radius: 20px;
          padding: 1.6rem;
          transition: all 0.35s ease;
          backdrop-filter: blur(2px);
          opacity: 0;
          transform: translateY(20px);
          animation: skCardReveal 0.5s ease forwards;
        }

        .sk-card:nth-child(1) { animation-delay: 0.05s; }
        .sk-card:nth-child(2) { animation-delay: 0.1s; }
        .sk-card:nth-child(3) { animation-delay: 0.15s; }
        .sk-card:nth-child(4) { animation-delay: 0.2s; }
        .sk-card:nth-child(5) { animation-delay: 0.25s; }
        .sk-card:nth-child(6) { animation-delay: 0.3s; }

        .sk-card:hover {
          border-color: rgba(0,245,212,0.35);
          background: rgba(0,245,212,0.04);
          transform: translateY(-6px) scale(1.01);
          box-shadow: 0 15px 30px -12px rgba(0,0,0,0.5);
        }

        .sk-card-icon {
          width: 56px;
          height: 56px;
          background: rgba(0,245,212,0.08);
          border: 1px solid rgba(0,245,212,0.2);
          border-radius: 18px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 2rem;
          margin-bottom: 1.2rem;
          transition: 0.2s;
        }

        .sk-card:hover .sk-card-icon {
          border-color: var(--accent);
          background: rgba(0,245,212,0.15);
          transform: scale(1.02);
        }

        .sk-card-title {
          font-size: 1.35rem;
          font-weight: 700;
          color: #ffffff;
          letter-spacing: -0.02em;
          margin-bottom: 1rem;
          background: linear-gradient(135deg, #fff, #b0b8ff);
          -webkit-background-clip: text;
          background-clip: text;
          color: transparent;
        }

        .sk-skills-list {
          display: flex;
          flex-wrap: wrap;
          gap: 0.6rem;
        }

        .sk-skill-badge {
          background: rgba(123,97,255,0.15);
          border: 1px solid rgba(123,97,255,0.3);
          border-radius: 40px;
          padding: 0.3rem 1rem;
          font-family: 'Space Mono', monospace;
          font-size: 0.7rem;
          font-weight: 500;
          color: #cbcbff;
          letter-spacing: 0.02em;
          transition: all 0.2s;
        }

        .sk-skill-badge:hover {
          background: rgba(0,245,212,0.2);
          border-color: var(--accent);
          color: var(--accent);
          transform: translateY(-2px);
        }

        .sk-empty {
          text-align: center;
          padding: 3rem;
          color: #8e8eaa;
          font-family: 'Space Mono', monospace;
          font-size: 0.9rem;
          border: 1px dashed rgba(0,245,212,0.2);
          border-radius: 32px;
          background: rgba(0,0,0,0.2);
          grid-column: 1 / -1;
        }

        @keyframes skFadeUp {
          from { opacity: 0; transform: translateY(24px); }
          to   { opacity: 1; transform: translateY(0); }
        }

        @keyframes skGrad {
          0%   { background-position: 0% center; }
          100% { background-position: 200% center; }
        }

        @keyframes skCardReveal {
          0% { opacity: 0; transform: translateY(20px); }
          100% { opacity: 1; transform: translateY(0); }
        }

        @media (max-width: 780px) {
          .sk-section { padding: 4rem 1.5rem; }
          .sk-grid { grid-template-columns: 1fr; }
        }

        @media (max-width: 480px) {
          .sk-filters { gap: 0.5rem; }
          .sk-filter-btn { padding: 0.4rem 0.9rem; font-size: 0.6rem; }
          .sk-skill-badge { font-size: 0.65rem; padding: 0.25rem 0.8rem; }
        }
      `}</style>

      <section id="skills" className="sk-section">
        <div className="sk-inner">
          <div className="sk-heading">
            <div className="sk-tag">Technical expertise</div>
            <h2 className="sk-main-title">
              My <span className="sk-accent">Skillset</span>
            </h2>
          </div>

          <div className="sk-filters">
            <button
              className={`sk-filter-btn ${activeFilter === 'all' ? 'active' : ''}`}
              onClick={() => setActiveFilter('all')}
            >
              All Skills
            </button>
            {SKILL_CATEGORIES.map(cat => (
              <button
                key={cat.id}
                className={`sk-filter-btn ${activeFilter === cat.id ? 'active' : ''}`}
                onClick={() => setActiveFilter(cat.id)}
              >
                {cat.icon} {cat.title}
              </button>
            ))}
          </div>

          <div className="sk-grid">
            {filteredCategories.length === 0 ? (
              <div className="sk-empty">✨ No skills found in this category.</div>
            ) : (
              filteredCategories.map((category, idx) => (
                <div key={category.id} className="sk-card">
                  <div className="sk-card-icon">{category.icon}</div>
                  <h3 className="sk-card-title">{category.title}</h3>
                  <div className="sk-skills-list">
                    {category.skills.map((skill, i) => (
                      <span key={i} className="sk-skill-badge">{skill}</span>
                    ))}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </section>
    </>
  );
};

export default Skills;